import { type TimestampStrategy, messageLayoutTokens } from '@/shared/theme/message-layout';
import { formatDateCompact, formatDateFull } from '@/shared/utilities';
import { useIsMobile } from '@/shared/utilities/responsive-spacing';
import { Tooltip } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';

interface TimestampDisplayProps {
  timestamp: Dayjs | Date | string | number;
  strategy?: TimestampStrategy;
  isGrouped?: boolean;
  className?: string;
}

export const TimestampDisplay = memo<TimestampDisplayProps>(
  ({ timestamp, strategy = 'inline', isGrouped = false, className }) => {
    const shortTime = useMemo(() => formatDateCompact(timestamp), [timestamp]);
    const fullTime = useMemo(() => formatDateFull(timestamp), [timestamp]);
    const isMobile = useIsMobile();
    const [showFull, setShowFull] = useState(false);

    // Toggle full time display on mobile tap
    const handleTap = useCallback(() => {
      if (isMobile && strategy === 'hover') {
        setShowFull((prev) => !prev);
      }
    }, [isMobile, strategy]);

    // Grouped strategy: hide timestamp when grouped
    if (strategy === 'grouped' && isGrouped) {
      return null;
    }

    const displayTime = isMobile && showFull ? fullTime : shortTime;

    const timeElement = (
      <span
        className={className}
        style={{
          fontSize: messageLayoutTokens.timestamp.fontSize,
          opacity: messageLayoutTokens.timestamp.opacity,
          cursor: isMobile && strategy === 'hover' ? 'pointer' : undefined
        }}
        onClick={handleTap}
        onKeyDown={(e) => e.key === 'Enter' && handleTap()}
        role={isMobile && strategy === 'hover' ? 'button' : undefined}
        tabIndex={isMobile && strategy === 'hover' ? 0 : undefined}
      >
        {displayTime}
      </span>
    );

    // Hover strategy on desktop: show full date on hover tooltip
    if (strategy === 'hover' && !isMobile) {
      return (
        <Tooltip title={fullTime} enterDelay={messageLayoutTokens.timestamp.hoverDelay}>
          {timeElement}
        </Tooltip>
      );
    }

    // Inline strategy (default): just show the time
    return timeElement;
  }
);

TimestampDisplay.displayName = 'TimestampDisplay';
